// Le drill en plein écran (D-098), repris de Playbook BCL : réaction (un signal géant, tiré au hasard),
// circuit (compteur, compte à rebours, signaux, repos), fractionné (effort orange, récup bleue). Écran
// maintenu allumé, plein écran dans les deux sens, parade iOS (drill-engine.ts). Le temps du circuit et
// du fractionné est calé sur l'horloge (départ + pauses) : juste après une mise en veille.
// L'arrêt se fait en deux touchers (« Arrêter ? », comme la séance en direct) : un ballon sur l'écran ne
// coupe pas tout ; sans second toucher, le drill reprend. À la fin, on revient à l'écran de départ.
// D-161 : un drill de réaction « en séquence » part dans drill-seq-run.ts (le même cadre, le même arrêt) ; une étape
// « Signaux » de circuit qui reprend une séquence la fait tourner ; les signes tirés prennent le sens que leur donne
// l'équipe qui lance (« Les signes de l'équipe »).
import './drills.css';
import { teamRoster, type Team } from '../lib/api';
import { clubDrillImages, noteDrillRun, type Drill } from '../lib/drills-api';
import {
  PHASE_LABEL, STAGE_LABEL, counterTicks, counterValue, cueLength, expandPool, fmtClock, fmtShort, intervalTimeline, phaseAt, pickCue, readCircuit,
  readInterval, readReaction, stageDone, stagePreview, totalSignals,
  type CircuitConfig, type Cue, type IntervalConfig, type Phase, type ReactionConfig, type Stage,
} from '../domain/drills';
import { displayName } from '../domain/labels';
import { beep, engage, readyToRun, speak, unlockAudio } from './drill-engine';
import { cueHtml } from './drill-view';
import { $, html, mount } from './dom';
import { teamSignNames } from '../lib/signs-api';
import { runSequence, seqPlayer, stepsBar, type SeqPlayer } from './drill-seq-run';

export interface Overrides { reaction?: Partial<ReactionConfig>; fractionne?: Partial<IntervalConfig>; circuit?: Partial<CircuitConfig> }
export interface RunOpts {
  /** L'équipe qui lance (les prénoms, le lancement noté) ; la joueuse : son équipe. */
  team?: Team | null;
  /** Le staff : images du club, lancement noté. */
  staff?: boolean;
  /** Ajustements pour ce lancement seulement (jamais enregistrés). */
  overrides?: Overrides;
  /** Un essai depuis l'éditeur : rien n'est noté. */
  trial?: boolean;
  onClose?: () => void;
}

/** Lance un drill en plein écran. */
export async function launchDrill(d: Pick<Drill, 'id' | 'club_id' | 'title' | 'kind' | 'config'>, opts: RunOpts = {}): Promise<void> {
  unlockAudio();   // pendant le geste : Safari iOS ne joue rien sinon
  const needsRoster = d.kind !== 'fractionne';
  const [roster, imgs] = await Promise.all([
    needsRoster && opts.team ? teamRoster(opts.team.id).then(r => r.filter(x => x.kind === 'member').map(x => ({ id: x.person_id, name: displayName(x) }))).catch(() => []) : Promise.resolve([]),
    needsRoster && opts.staff ? clubDrillImages(d.club_id).catch(() => []) : Promise.resolve([]),
  ]);
  const images = new Map(imgs.map(i => [i.id, i.data]));
  // Les signes de l'équipe (leur sens), seulement si le drill tire des signes.
  const signNames = needsRoster && JSON.stringify(d.config ?? {}).includes('"signs"') ? await teamSignNames(opts.team?.id) : new Map<string, string>();
  const go = () => {
    if (opts.staff && !opts.trial && d.id) void noteDrillRun(d.id, opts.team?.id ?? null).catch(() => undefined);
    const o = opts.overrides ?? {};
    if (d.kind === 'fractionne') runInterval(d.title, { ...readInterval(d.config), ...o.fractionne }, opts);
    else if (d.kind === 'circuit') runCircuit(d.title, { ...readCircuit(d.config), ...o.circuit }, { roster, images, signNames }, opts);
    else {
      const r = { ...readReaction(d.config), ...o.reaction };
      if (r.mode === 'sequence' && r.sequence) runSequence(d.title, r.sequence, { roster, images, signNames }, opts, { frame, stopButton, endScreen });
      else runReaction(d.title, r, { roster, images, signNames }, opts);
    }
  };
  if (readyToRun(go)) go();
}

// --- Le cadre commun : l'hôte, l'arrêt en deux touchers, Échap -----------------------------------------
interface Frame { host: HTMLElement; close: () => void }
function frame(cls: string, title: string, opts: RunOpts, cleanup: () => void): Frame {
  const host = document.createElement('div');
  host.className = cls;
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', title);
  document.body.append(host);
  const release = engage(host, title);
  let closed = false;
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') host.querySelector<HTMLButtonElement>('[data-x]')?.click(); };
  document.addEventListener('keydown', onKey);
  const close = () => {
    if (closed) return; closed = true;
    cleanup();
    document.removeEventListener('keydown', onKey);
    release();
    host.remove();
    opts.onClose?.();
  };
  return { host, close };
}

/** Le bouton ×, en deux touchers : le premier met en pause et demande « Arrêter ? » ; sans second toucher, ça reprend. */
function stopButton(host: HTMLElement, pauseFor: (on: boolean) => void, close: () => void, hint?: (t: string | null) => void): void {
  const x = host.querySelector<HTMLButtonElement>('[data-x]');
  if (!x) return;
  let armed = false, t = 0;
  x.addEventListener('click', e => {
    e.stopPropagation();
    if (armed) { window.clearTimeout(t); close(); return; }
    armed = true; x.classList.add('armed'); x.textContent = 'Arrêter ?';
    pauseFor(true);
    hint?.('Touche encore « Arrêter ? » pour finir · sinon ça reprend');
    t = window.setTimeout(() => {
      armed = false; x.classList.remove('armed'); x.textContent = '×';
      hint?.(null);
      pauseFor(false);
    }, 4000);
  });
}

const endScreen = (icon: string, title: string, line: string) => html`
  <div class="dr-end"><span class="ic" aria-hidden="true">${icon}</span><h2>${title}</h2><p>${line}</p>
    <div class="row"><button type="button" class="btn-big" data-again>🔁 Rejouer</button><button type="button" class="btn-big ghost" data-close>Fermer</button></div></div>`;

// -----------------------------------------------------------------------------------------------
// Réaction
// -----------------------------------------------------------------------------------------------
type RunCtx = { roster: { id: string; name: string }[]; images: Map<string, string>; signNames: Map<string, string> };
function runReaction(title: string, cfg: ReactionConfig, ctx: RunCtx, opts: RunOpts): void {
  const pool = expandPool(cfg.stimuli, { roster: ctx.roster, images: [...ctx.images.keys()], signNames: ctx.signNames });
  let timer = 0, antic = 0, clock = 0;
  const f = frame('dr-run', title, opts, () => { clearTimeout(timer); clearInterval(antic); clearInterval(clock); });
  const { host } = f;
  if (!pool.length) {
    mount(host, html`<button type="button" class="x" data-x aria-label="Fermer">×</button>${endScreen('🤷', 'Aucun signal à montrer', 'Ajoute des flèches, des couleurs ou des prénoms au drill.')}`);
    host.querySelector('[data-again]')?.remove();
    $(host, '[data-x]')!.addEventListener('click', f.close);
    $(host, '[data-close]')!.addEventListener('click', f.close);
    return;
  }
  const total = totalSignals(cfg);
  const minutes = cfg.duration.mode === 'minutes';
  let count = 0, prev: Cue | null = null, paused = false, done = false;
  let endAt = 0, leftAtPause = 0, t0 = 0;
  mount(host, html`
    <span class="bar" data-bar></span><button type="button" class="x" data-x aria-label="Arrêter le drill">×</button>
    <span class="antic" data-antic hidden><b></b><small>prochain signal</small></span>
    <div class="dr-cue" data-cue aria-live="polite"></div>
    <span class="hint" data-hint>Écran maintenu allumé · tourne le téléphone si tu veux</span>
    <span class="prog"><i data-prog style="width:0"></i></span>`);
  const bar = $(host, '[data-bar]')!, cueEl = $(host, '[data-cue]')!, prog = $(host, '[data-prog]')!, hintEl = $(host, '[data-hint]')!, anticEl = $(host, '[data-antic]')!;
  let hintT = window.setTimeout(() => hintEl.classList.add('out'), 4000);
  const hint = (t: string | null) => { clearTimeout(hintT); if (t) { hintEl.textContent = t; hintEl.classList.remove('out'); } else hintEl.classList.add('out'); };
  const paint = () => {
    const now = Date.now();
    if (minutes) {
      const left = paused ? leftAtPause : Math.max(0, endAt - now);
      bar.textContent = `${title} · ${fmtClock(left)} restantes`;
      prog.style.width = `${Math.min(100, 100 * (1 - left / (cfg.duration.value * 60_000)))}%`;
    } else {
      bar.textContent = `${title} · ${count} / ${total}`;
      prog.style.width = `${Math.min(100, (100 * count) / total)}%`;
    }
  };
  const stopAntic = () => { clearInterval(antic); anticEl.hidden = true; };
  const startAntic = (ms: number) => {
    const end = Date.now() + ms;
    anticEl.hidden = false;
    const upd = () => { const r = end - Date.now(); if (r <= 0) { stopAntic(); return; } anticEl.querySelector('b')!.textContent = String(Math.ceil(r / 1000)); };
    upd(); antic = window.setInterval(upd, 100);
  };
  const finish = () => {
    done = true; clearTimeout(timer); clearInterval(clock); stopAntic();
    prog.style.width = '100%';
    bar.textContent = title;
    $(host, '[data-x]')!.hidden = true;
    mount(cueEl, endScreen('✓', 'Terminé', `${count} ${count > 1 ? 'signaux' : 'signal'}`));
    $(cueEl, '[data-close]')!.addEventListener('click', f.close);
    $(cueEl, '[data-again]')!.addEventListener('click', () => { $(host, '[data-x]')!.hidden = false; start(); });
  };
  const next = () => {
    if (paused || done) return;
    if ((!minutes && count >= total) || (minutes && Date.now() >= endAt)) { finish(); return; }
    const cue = pickCue(pool, Math.random, prev);
    prev = cue;
    mount(cueEl, cueHtml(cue, ctx.images));
    stopAntic();
    if (cfg.beep) beep();
    const len = cueLength(cfg, count, total);
    count++;
    paint();
    timer = window.setTimeout(() => {
      if (paused || done) return;
      if (cfg.delay_ms > 0) {
        mount(cueEl, html``);
        if (cfg.anticipation && cfg.delay_ms >= 1000) startAntic(cfg.delay_ms);
        timer = window.setTimeout(next, cfg.delay_ms);
      } else next();
    }, len);
  };
  const start = () => {
    count = 0; prev = null; done = false; paused = false; t0 = Date.now();
    endAt = t0 + cfg.duration.value * 60_000;
    clearInterval(clock);
    if (minutes) clock = window.setInterval(paint, 250);
    next();
  };
  stopButton(host, on => {
    if (done) return;
    // En pause : le noir, le décompte figé ; la reprise tire un nouveau signal.
    if (on) { paused = true; clearTimeout(timer); clearInterval(antic); leftAtPause = Math.max(0, endAt - Date.now()); mount(cueEl, html``); paint(); }
    else { paused = false; endAt = Date.now() + leftAtPause; next(); }
  }, f.close, hint);
  start();
}

// -----------------------------------------------------------------------------------------------
// Circuit
// -----------------------------------------------------------------------------------------------
function runCircuit(title: string, cfg: CircuitConfig, ctx: RunCtx, opts: RunOpts): void {
  const stages = cfg.stages;
  let driver = 0, cueT = 0, seq: SeqPlayer | null = null;
  const f = frame('dr-run has-ctl', title, opts, () => { clearInterval(driver); clearTimeout(cueT); });
  const { host } = f;
  let idx = 0, epoch = 0, pausedAcc = 0, pausedAt: number | null = null, resting = false, restMs = 0, ticks = -1, lastSec: number | null = null, halfBeep = false;
  let startedAt = 0, done = false, prev: Cue | null = null, pool: Cue[] = [];
  mount(host, html`
    <span class="bar two" data-bar></span><button type="button" class="x" data-x aria-label="Arrêter le circuit">×</button>
    <div class="dr-cue" data-cue aria-live="polite"></div>
    <div class="dq-steps dq-steps-ctl" data-steps aria-hidden="true"></div>
    <div class="dr-ctl" data-ctl><button type="button" class="a" data-pause>❚❚ Pause</button><button type="button" class="b" data-skip>Suivant ›</button></div>
    <span class="prog"><i data-prog style="width:0"></i></span>`);
  const bar = $(host, '[data-bar]')!, cueEl = $(host, '[data-cue]')!, prog = $(host, '[data-prog]')!, ctl = $(host, '[data-ctl]')!, stepsEl = $(host, '[data-steps]')!;
  const pauseB = $<HTMLButtonElement>(host, '[data-pause]')!, skipB = $<HTMLButtonElement>(host, '[data-skip]')!;
  const elapsed = () => Math.max(0, Date.now() - epoch - pausedAcc - (pausedAt !== null ? Date.now() - pausedAt : 0));
  const stage = () => stages[idx]!;

  const renderStage = () => {
    const s = stage();
    host.classList.remove('rest');
    mount(bar, html`<span>${title}</span><span>Étape ${idx + 1}/${stages.length}${s.label ? ` · ${s.label}` : ''}</span>`);
    skipB.textContent = 'Suivant ›';
    prog.style.width = `${(100 * idx) / stages.length}%`;
    if (s.type === 'counter') mount(cueEl, html`<div class="big"><span class="lb">${s.label}</span><span class="val" data-val>${counterValue(s, 0)}</span></div>`);
    else if (s.type === 'countdown') mount(cueEl, html`<div class="big"><span class="lb">${s.label}</span><span class="clock" data-clock>${fmtClock((s.seconds ?? 30) * 1000)}</span></div>`);
    else mount(cueEl, html``);
  };
  const cueLoop = (s: Stage) => {
    clearTimeout(cueT);
    if (pausedAt !== null || resting || stage() !== s || done) return;
    const c = pickCue(pool, Math.random, prev);
    prev = c;
    mount(cueEl, cueHtml(c, ctx.images));
    beep();
    const len = cueLength({ length_ms: s.length_ms ?? 2000, rhythm: 'fixe', delay_ms: 0, duration: { mode: 'signals', value: 1 }, stimuli: {}, anticipation: false, beep: true }, 0, 1);
    cueT = window.setTimeout(() => {
      if ((s.delay_ms ?? 0) > 0) { mount(cueEl, html``); cueT = window.setTimeout(() => cueLoop(s), s.delay_ms); } else cueLoop(s);
    }, len);
  };
  const enter = (i: number) => {
    idx = i; epoch = Date.now(); pausedAcc = 0; pausedAt = null; resting = false; ticks = -1; lastSec = null; prev = null; seq = null;
    clearTimeout(cueT);
    mount(stepsEl, html``);
    const s = stage();
    renderStage();
    if (cfg.voice) speak(s.type === 'counter' ? `${s.label || STAGE_LABEL.counter}, ${counterValue(s, 0)}` : s.label || STAGE_LABEL[s.type]);
    if (s.type === 'countdown' && s.beep_start) beep(990);
    // Une séquence reprise d'un drill de réaction (D-161) : elle tourne, calée sur le temps de l'étape, jusqu'à sa fin.
    if (s.type === 'signals' && s.sequence) {
      seq = seqPlayer(cueEl, { ...s.sequence, repeat: { mode: 'minutes', value: 60 }, countdown: false, countdown_each: false }, ctx, (ev, plan) => mount(stepsEl, stepsBar(plan.tour(ev.tour).filter(x => x.kind !== 'decompte'), ev)));
      seq.tick(0);
    } else if (s.type === 'signals') {
      pool = expandPool(s.stimuli, { roster: ctx.roster, images: [...ctx.images.keys()] });
      if (!pool.length) mount(cueEl, html`<div class="big"><span class="lb">${s.label}</span><span class="lb">Aucun signal</span></div>`);
      else cueLoop(s);
    }
  };
  const enterRest = (ms: number) => {
    clearTimeout(cueT); seq = null; mount(stepsEl, html``);
    resting = true; restMs = ms; epoch = Date.now(); pausedAcc = 0; pausedAt = null; halfBeep = false;
    const nx = stages[idx + 1]!;
    host.classList.add('rest');
    mount(bar, html`<span>${title}</span><span>Repos avant l’étape ${idx + 2}/${stages.length}</span>`);
    skipB.textContent = 'Passer le repos ›';
    mount(cueEl, html`<div class="big"><span class="lb">💤 Repos</span><span class="clock" data-clock>${fmtClock(ms)}</span>
      <span class="nx"><small>Suivant</small><strong>${nx.label || STAGE_LABEL[nx.type]}</strong><small>${stagePreview(nx)}</small></span></div>`);
    if (cfg.voice) speak(`Repos. Ensuite : ${nx.label || STAGE_LABEL[nx.type]}`);
  };
  const advance = () => {
    if (done) return;
    if (idx + 1 >= stages.length) { finish(); return; }
    if (stage().rest_s > 0) { enterRest(stage().rest_s * 1000); return; }
    beep(); enter(idx + 1);
  };
  const tickFn = () => {
    if (done || pausedAt !== null) return;
    const e = elapsed();
    if (resting) {
      const rem = Math.max(0, restMs - e);
      const c = cueEl.querySelector('[data-clock]'); if (c) c.textContent = fmtClock(rem);
      if (!halfBeep && e >= restMs / 2) { halfBeep = true; beep(660, 0.15); }
      if (rem <= 0) { beep(); enter(idx + 1); }
      return;
    }
    const s = stage();
    if (s.type === 'counter') {
      const t = counterTicks(s, e);
      if (t !== ticks) {
        const first = ticks < 0;
        ticks = t;
        const v = counterValue(s, t);
        const el = cueEl.querySelector('[data-val]'); if (el) el.textContent = String(v);
        if (!first) { beep(660, 0.18); if (cfg.voice) speak(String(v)); }
      }
    } else {
      seq?.tick(e);
      const rem = Math.max(0, (s.seconds ?? 30) * 1000 - e);
      const c = cueEl.querySelector('[data-clock]'); if (c) c.textContent = fmtClock(rem);
      const sec = Math.ceil(rem / 1000);
      if (s.type === 'countdown' && s.beep_last3 && sec >= 1 && sec <= 3 && sec !== lastSec) { lastSec = sec; beep(); }
    }
    if (e > 0 && stageDone(s, e, cfg.auto)) advance();
  };
  const finish = () => {
    done = true; clearInterval(driver); clearTimeout(cueT); seq = null; mount(stepsEl, html``);
    host.classList.remove('rest');
    ctl.hidden = true; prog.style.width = '100%'; bar.textContent = title;
    $(host, '[data-x]')!.hidden = true;
    const s = Math.round((Date.now() - startedAt) / 1000);
    mount(cueEl, endScreen('🏁', 'Circuit terminé', `${stages.length} étape${stages.length > 1 ? 's' : ''} · ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`));
    $(cueEl, '[data-close]')!.addEventListener('click', f.close);
    $(cueEl, '[data-again]')!.addEventListener('click', start);
    if (cfg.voice) speak('Circuit terminé');
  };
  const setPaused = (on: boolean) => {
    if (done) return;
    if (on && pausedAt === null) { pausedAt = Date.now(); clearTimeout(cueT); }
    else if (!on && pausedAt !== null) { pausedAcc += Date.now() - pausedAt; pausedAt = null; if (seq) seq.reset(); else if (!resting && stage().type === 'signals' && pool.length) cueLoop(stage()); }
    pauseB.textContent = pausedAt !== null ? '▶ Reprendre' : '❚❚ Pause';
  };
  pauseB.addEventListener('click', () => setPaused(pausedAt === null));
  skipB.addEventListener('click', () => { if (resting) { beep(); enter(idx + 1); } else advance(); });
  stopButton(host, setPaused, f.close);
  const start = () => {
    if (!stages.length) { mount(cueEl, endScreen('🤷', 'Circuit vide', 'Ajoute une étape au circuit.')); cueEl.querySelector('[data-again]')?.remove(); ctl.hidden = true; $(cueEl, '[data-close]')!.addEventListener('click', f.close); return; }
    done = false; ctl.hidden = false; $(host, '[data-x]')!.hidden = false; startedAt = Date.now();
    clearInterval(driver);
    driver = window.setInterval(tickFn, 100);
    enter(0);
  };
  start();
}

// -----------------------------------------------------------------------------------------------
// Fractionné
// -----------------------------------------------------------------------------------------------
const nextText = (p: Phase | undefined) => (p ? html`Ensuite : <b>${PHASE_LABEL[p.type]} ${fmtShort(p.ms / 1000)}</b>${p.sub ? ` · ${p.sub}` : ''}` : html`Ensuite : <b>fin</b>`);

function runInterval(title: string, cfg: IntervalConfig, opts: RunOpts): void {
  const tl = intervalTimeline(cfg);
  const total = tl.reduce((n, p) => n + p.ms, 0);
  let driver = 0;
  const f = frame('dr-itv', title, opts, () => clearInterval(driver));
  const { host } = f;
  let epoch = 0, pausedAcc = 0, pausedAt: number | null = null, idx = -1, lastSec: number | null = null, done = false, startedAt = 0;
  const elapsed = () => Math.max(0, Date.now() - epoch - pausedAcc - (pausedAt !== null ? Date.now() - pausedAt : 0));
  const draw = () => mount(host, html`
    <div class="top"><span><span>${title}</span><span data-cyc></span></span><button type="button" class="x" data-x aria-label="Arrêter le fractionné">×</button></div>
    <div class="mid" aria-live="polite"><span class="ph" data-ph></span><span class="tm" data-tm></span><span class="cy" data-sub></span><span class="nx" data-nx></span></div>
    <div class="bot"><div class="frz">${tl.map(p => html`<i style="flex:${p.ms};background:${p.type === 'work' ? '#FFB27A' : p.type === 'rest' ? 'rgba(0,0,0,.28)' : '#9AA1AA'}"></i>`)}<span class="now" data-now></span></div>
      <div class="btns"><button type="button" class="a" data-pause>❚❚ Pause</button><button type="button" class="b" data-skip>Suivant ›</button></div></div>`);
  draw();
  const q = (s: string) => $(host, s)!;
  const enterPhase = (i: number) => {
    idx = i; lastSec = null;
    const p = tl[i]!;
    host.classList.remove('work', 'rest', 'warmup', 'cooldown');
    host.classList.add(p.type);
    q('[data-ph]').textContent = PHASE_LABEL[p.type];
    q('[data-sub]').textContent = p.sub ?? '';
    mount(q('[data-nx]'), nextText(tl[i + 1]));
    if (p.type === 'work' && cfg.beeps) beep(990);
    if (cfg.voice) speak(p.type === 'work' && p.of ? `Effort, cycle ${p.cycle} sur ${p.of}` : PHASE_LABEL[p.type]);
  };
  const tickFn = () => {
    if (done) return;
    const e = elapsed();
    const cur = phaseAt(tl, e);
    if (!cur) { finish(); return; }
    if (cur.index !== idx) enterPhase(cur.index);
    q('[data-tm]').textContent = fmtClock(cur.remaining);
    const p = cur.phase;
    q('[data-cyc]').textContent = `${p.of ? `Cycle ${p.cycle} / ${p.of} · ` : ''}Reste ${fmtClock(total - e)}`;
    q('[data-now]').style.left = `${Math.min(100, (100 * e) / total)}%`;
    const sec = Math.ceil(cur.remaining / 1000);
    if (cfg.beeps && pausedAt === null && sec >= 1 && sec <= 3 && sec !== lastSec) { lastSec = sec; beep(); }
  };
  const setPaused = (on: boolean) => {
    if (done) return;
    if (on && pausedAt === null) pausedAt = Date.now();
    else if (!on && pausedAt !== null) { pausedAcc += Date.now() - pausedAt; pausedAt = null; }
    host.classList.toggle('paused', pausedAt !== null);
    q('[data-pause]').textContent = pausedAt !== null ? '▶ Reprendre' : '❚❚ Pause';
  };
  const skip = () => {
    if (done) return;
    const nx = tl[idx + 1];
    if (!nx) { finish(); return; }
    // On décale le départ : la suite reste calée sur l'horloge.
    epoch = Date.now() - nx.start - pausedAcc - (pausedAt !== null ? Date.now() - pausedAt : 0);
    tickFn();
  };
  const wire = () => {
    q('[data-pause]').addEventListener('click', () => setPaused(pausedAt === null));
    q('[data-skip]').addEventListener('click', skip);
    stopButton(host, setPaused, f.close);
  };
  const finish = () => {
    done = true; clearInterval(driver);
    host.classList.remove('work', 'rest', 'warmup', 'cooldown', 'paused');
    host.style.background = '#0f1420';
    const s = Math.round((Date.now() - startedAt) / 1000);
    mount(host, html`<div></div><div class="mid">${endScreen('🏁', 'Fractionné terminé', `${cfg.cycles} cycle${cfg.cycles > 1 ? 's' : ''} · ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`)}</div><div></div>`);
    q('[data-close]').addEventListener('click', f.close);
    q('[data-again]').addEventListener('click', start);
    if (cfg.voice) speak('Fractionné terminé');
  };
  const start = () => {
    if (!tl.length) { finish(); return; }
    host.style.background = '';
    draw(); wire();
    done = false; idx = -1; epoch = Date.now(); startedAt = epoch; pausedAcc = 0; pausedAt = null;
    clearInterval(driver);
    driver = window.setInterval(tickFn, 100);
    tickFn();
  };
  start();
}
